import { motion } from 'framer-motion';
import { Download, BookOpen, ArrowRight, Clock } from 'lucide-react';
import { FloatingPaths } from '@LandingFunctionalDisplay/BackgroundPaths';
import TopPanel from '@CommonFunctionalDisplay/TopPanel';
import Footer from '@CommonFunctionalDisplay/Footer';
import useSEO from '@Hooks/useSEO';
import './EbooksPage.scss';

const EBOOKS = [
    {
        tag: 'Fundamentals',
        tagColor: 'blue',
        title: 'The Complete Guide to Ticket Management',
        desc: 'Everything you need to build a structured, scalable ticket management system — from intake form design to SLA policies and analytics.',
        pages: '42 pages',
        topics: ['Form design best practices', 'SLA target setting', 'Agent workflow optimisation', 'Analytics that matter'],
        cta: 'Download Free',
        featured: true,
    },
    {
        tag: 'Communication',
        tagColor: 'green',
        title: 'The WhatsApp Support Playbook',
        desc: 'A practical guide for teams adopting WhatsApp as a support channel — notification templates, escalation flows, and customer communication scripts.',
        pages: '28 pages',
        topics: ['WhatsApp notification strategy', 'Message templates that work', 'Escalation communication', 'Customer expectation setting'],
        cta: 'Download Free',
        featured: false,
    },
    {
        tag: 'Operations',
        tagColor: 'purple',
        title: 'SLA Best Practices for Growing Teams',
        desc: 'How to design SLA policies that are achievable, fair to your team, and meaningful to your customers — with templates you can use today.',
        pages: '24 pages',
        topics: ['Priority matrix design', 'Response vs resolution SLAs', 'Escalation matrix setup', 'SLA reporting and governance'],
        cta: 'Download Free',
        featured: false,
    },
    {
        tag: 'Leadership',
        tagColor: 'orange',
        title: 'Building a World-Class Support Operation',
        desc: 'A strategic guide for CXOs and operations leaders who want to turn customer support into a competitive differentiator.',
        pages: '56 pages',
        topics: ['Support team structure', 'KPI framework for support', 'Technology selection', 'Building a support culture'],
        cta: 'Download Free',
        featured: false,
    },
];

const TAG_COLORS = {
    blue:   { bg: 'rgba(26,111,181,0.18)',  text: '#3b9aee' },
    green:  { bg: 'rgba(5,150,105,0.18)',   text: '#10b981' },
    purple: { bg: 'rgba(124,58,237,0.18)',  text: '#a78bfa' },
    orange: { bg: 'rgba(234,88,12,0.18)',   text: '#fb923c' },
};

function Reveal({ children, className = '', delay = 0 }) {
    return (
        <motion.div className={className}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay }}
        >{children}</motion.div>
    );
}

export default function EbooksPage() {
    const cn = 'ebooksPage';

    const seo = useSEO({
        title:       'Ebooks & Guides — Deep Dives Into Support Excellence',
        description: 'Free ebooks on ticket management, WhatsApp support, SLA best practices, and building world-class support operations. Written by KaizIQ support experts.',
        canonical:   'https://resolve.kaiziq.io/#/ebooks',
        schema: {
            '@context': 'https://schema.org',
            '@type':    'CollectionPage',
            name:       'KaizIQ Resolve Ebooks & Guides',
            url:        'https://resolve.kaiziq.io/#/ebooks',
            description: 'Free ebooks and practical guides on support operations, SLA management, and WhatsApp-native helpdesk.',
        },
    });

    return (
        <div className={cn}>
            {seo}
            <TopPanel />

            <section className={cn + '__hero'}>
                <FloatingPaths position={1} />
                <FloatingPaths position={-1} />
                <div className={cn + '__hero__inner'}>
                    <motion.div className={cn + '__hero__tag'} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                        Ebooks & Guides
                    </motion.div>
                    <motion.h1 className={cn + '__hero__title'} initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.1 }}>
                        Deep dives into support excellence
                    </motion.h1>
                    <motion.p className={cn + '__hero__sub'} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}>
                        Free, practical guides written by the KaizIQ team and our community of support leaders.
                    </motion.p>
                </div>
            </section>

            <section className={cn + '__books'}>
                {EBOOKS.map((book, i) => {
                    const tagStyle = TAG_COLORS[book.tagColor] || TAG_COLORS.blue;
                    return (
                        <Reveal key={i} className={cn + '__book' + (book.featured ? ' ' + cn + '__book--featured' : '')} delay={i * 0.1}>
                            <div className={cn + '__book__cover'}>
                                <BookOpen size={36} />
                                <span className={cn + '__book__tag'} style={{ background: tagStyle.bg, color: tagStyle.text }}>{book.tag}</span>
                            </div>
                            <div className={cn + '__book__content'}>
                                <h3 className={cn + '__book__title'}>{book.title}</h3>
                                <p className={cn + '__book__desc'}>{book.desc}</p>
                                <div className={cn + '__book__meta'}>
                                    <span><BookOpen size={13} /> {book.pages}</span>
                                    <span><Clock size={13} /> Free download</span>
                                </div>
                                <ul className={cn + '__book__topics'}>
                                    {book.topics.map((t, ti) => (
                                        <li key={ti}>{t}</li>
                                    ))}
                                </ul>
                                <button className={cn + '__book__cta'}>
                                    <Download size={16} /> {book.cta}
                                </button>
                            </div>
                        </Reveal>
                    );
                })}
            </section>

            <section className={cn + '__newsletter'}>
                <Reveal className={cn + '__newsletter__inner'}>
                    <h2 className={cn + '__newsletter__title'}>Get new guides in your inbox</h2>
                    <p className={cn + '__newsletter__sub'}>We publish a new ebook or playbook every month. Subscribe to receive them directly.</p>
                    <div className={cn + '__newsletter__form'}>
                        <input type="email" placeholder="you@company.com" className={cn + '__newsletter__input'} />
                        <button className={cn + '__newsletter__btn'}>Subscribe <ArrowRight size={15} /></button>
                    </div>
                </Reveal>
            </section>

            <Footer />
        </div>
    );
}
